import { createApp } from 'vue'
import bus from '@/shared/eventBus'
import { pinia } from './stores/pinia'
import registerGlobals from './bootstrap'
import { useAuthStore } from './stores/auth'
import axios from './util/axios'
import Echo from './util/Echo'
import Config from './util/config'
import LocalConfig from './config'
import AppLayout from './views/layouts/App'
import router from './router'

export class LiveCaller {
    version = import.meta.env.VUE_APP_VERSION

    constructor() {
        this.bootingCallbacks = []
        this.config = new Config(LocalConfig)
        this.translations = {}
        this.echo = new Echo()
        // Transport bus for the CoBrowse SDK (`transport.bus.emit`). Routes to
        // the app's $-prefixed event bus so SDK-emitted cobrowse events reach
        // the same listeners the app subscribes to via `LiveCaller.$on(...)`.
        this.bus = {
            emit: (type, ...args) => bus.$emit(type, ...args),
            on: (type, callback) => bus.$on(type, callback),
            off: (type, callback) => bus.$off(type, callback),
        }
    }

    /**
     * Register a callback to be called before LiveCaller starts. This is used to bootstrap
     * addons, tools, custom fields, or anything else LiveCaller needs
     */
    booting(callback) {
        this.bootingCallbacks.push(callback)
    }

    /**
     * Execute all of the booting callbacks.
     */
    boot() {
        this.bootingCallbacks.forEach((callback) => callback(this, router))

        this.bootingCallbacks = []
    }

    /**
     * Start the LiveCaller app by calling each of the tool's callbacks and then creating
     * the underlying Vue instance.
     *
     */
    liftOff() {
        this.boot()
        const app = createApp(AppLayout)
        Object.assign(app.config.globalProperties, {
            $config: this.config,
            $can: (permission) => {
                const { user } = useAuthStore(pinia)
                return user && user.can(permission)
            },
            $echo: this.echo,
            $version: this.version,
        })
        app.use(pinia)
        app.use(router)
        registerGlobals(app)
        this.app = app.mount('#live-caller-app')
    }

    request(options) {
        return this.constructor.request(options)
    }

    /**
     * Return an axios instance configured to make requests to LiveCaller's API
     * and handle certain response codes.
     */
    static request(options) {
        if (options !== undefined) {
            return axios(options)
        }

        return axios
    }

    /**
     * Event bus pass-through (implementation in @/shared/eventBus). Kept on
     * the LiveCaller singleton because realtime code app-wide subscribes via
     * LiveCaller.$on('ws:…' / 'conversations.…') and so on.
     */
    $on(type, callback) {
        bus.$on(type, callback)
        return this
    }

    $once(type, callback) {
        bus.$once(type, callback)
        return this
    }

    $off(type, callback) {
        bus.$off(type, callback)
        return this
    }

    $emit(type, ...args) {
        bus.$emit(type, ...args)
        return this
    }
}

const LC = new LiveCaller()
window.LiveCallerApp = LC
export default LC
